import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../../components/PageFooter';
import CartAddedToast from '../../components/CartAddedToast';
import { useHairSession } from '../../context/HairSessionContext';
import { getSessionId } from '../../utils/sessionManager';
import { addCartItem } from '../../utils/cartApi';
import { useHairReportData } from './useHairReportData';
import HairReportNav from './HairReportNav';
import HairDiagnosticSection from './HairDiagnosticSection';
import MagnifiedAnalysisSection from './MagnifiedAnalysisSection';
import HairRoutineSection from './HairRoutineSection';
import HairInsightReportTreatmentsSection from '../HairInsight/HairInsightReportTreatmentsSection';
import { getHairSalonApiBaseUrl } from '../../utils/hairSalonApiDeps';
import {
  scheduleSyncAnalysisPendingServices,
  treatmentCartToPendingServices,
  confirmAnalysisServicesAndStart,
  persistTreatmentCart,
  modernConfirm,
  useHairSalonOrgLogo,
  readStoredFranchiseBrandName,
  navigateHairFlowHome,
  resolveHairSalonVisitSessionId,
} from '../../utils/hairSalonFlowDeps';
import '../../../../../enterprise/salon/pages/SkinRoutinePage.css';
import '../../../../../naturals/salon/pages/SkinRoutinePage.css';
import '../../styles/PageBackground.css';
import '../../styles/ActionButtons.css';
import './HairReportPage.css';

function HairReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionLandingPath, token } = useHairSession();
  const sessionId =
    location.state?.sessionId || resolveHairSalonVisitSessionId() || getSessionId();

  const hairResults = location.state?.hairResults || null;
  const userData = location.state?.userData || null;
  const aqiData = location.state?.aqiData || {
    temp: 34.3,
    aqi: 'Moderate',
    uv: 'Extreme',
    location: 'Sholinganallur, Tamil Nadu',
  };

  const [cartCount, setCartCount] = useState(0);
  const [cartToastVisible, setCartToastVisible] = useState(false);
  const [hairServiceSelections, setHairServiceSelections] = useState({});
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [serviceConfirmNotice, setServiceConfirmNotice] = useState('');
  const { orgLogoUrl, showLogo, onLogoError } = useHairSalonOrgLogo();
  const orgName = readStoredFranchiseBrandName();

  useEffect(() => {
    const prev = document.title;
    document.title = 'Hair Health Analysis Report | nyafaz';
    return () => {
      document.title = prev;
    };
  }, []);

  const {
    dbRecommendations,
    recsLoading,
    routineData,
    routineLoading,
    recommendedTreatments,
    treatmentsLoading,
    treatmentsError,
  } = useHairReportData({
    sessionId,
    token,
    hairResults,
    userData,
    initialDbRecommendations: location.state?.dbRecommendations,
  });

  const handleHome = () => navigateHairFlowHome(navigate, sessionLandingPath);
  const handleBack = () => navigate(-1);

  const handleAddToCart = useCallback((product, context = {}) => {
    if (!product) return;
    setCartCount((prev) => prev + 1);
    setCartToastVisible(true);

    const scanId = hairResults?.scan_id ?? hairResults?.scanId ?? null;
    addCartItem({
      sessionId,
      scanId,
      token,
      userData,
      product,
      sourceSection: context.sourceSection,
      priceInr: context.priceInr,
      hairConcern: context.hairConcern,
    }).catch(() => {});
  }, [sessionId, token, userData, hairResults]);

  useEffect(() => {
    const lines = treatmentCartToPendingServices(hairServiceSelections, 'hair_analysis');
    scheduleSyncAnalysisPendingServices(lines, 'hair_analysis');
  }, [hairServiceSelections]);

  useEffect(() => {
    const base = getHairSalonApiBaseUrl();
    if (!base || !sessionId) return;
    persistTreatmentCart(base, sessionId, token, hairServiceSelections);
  }, [hairServiceSelections, sessionId, token]);

  const treatmentCartTotal = Object.values(hairServiceSelections || {}).reduce(
    (sum, row) => sum + (Number(row?.quantity) || 0),
    0,
  );

  const handleConfirmHairServices = async () => {
    if (confirmBusy || treatmentCartTotal <= 0) return;
    const ok = await modernConfirm(
      `Confirm ${treatmentCartTotal} selected service(s) for this visit? They will be started for the stylist desk.`,
      { title: 'Confirm services', confirmLabel: 'Confirm', cancelLabel: 'Cancel' },
    );
    if (!ok) return;
    setConfirmBusy(true);
    setServiceConfirmNotice('');
    try {
      const lines = treatmentCartToPendingServices(hairServiceSelections, 'hair_analysis');
      await confirmAnalysisServicesAndStart(lines, 'hair_analysis');
      setServiceConfirmNotice('Services confirmed and started for this visit.');
    } catch (err) {
      setServiceConfirmNotice(err?.message || 'Could not confirm services. Please try again.');
    } finally {
      setConfirmBusy(false);
    }
  };

  if (!hairResults) {
    return (
      <div className="hair-report-page">
        <div className="hair-report-error">
          <h2>Analysis Data Unavailable</h2>
          <button type="button" className="skin-action-button" onClick={handleHome}>
            Return to Start
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hair-report-page">
      <div className="hair-report-print-letterhead" aria-hidden="true">
        <div className="hair-report-print-brand">
          <span className="hair-report-print-logo">nyafaz</span>
          <span className="hair-report-print-doc-title">Hair Health Analysis Report</span>
        </div>
        <div className="hair-report-print-meta">
          <div>
            <span className="hair-report-print-label">Generated</span>
            <span>
              {new Date().toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}{' '}
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {sessionId ? (
            <div>
              <span className="hair-report-print-label">Session</span>
              <span className="hair-report-print-session">{sessionId}</span>
            </div>
          ) : null}
          {hairResults?.hair_score != null ? (
            <div>
              <span className="hair-report-print-label">Hair Score</span>
              <span>{hairResults.hair_score}</span>
            </div>
          ) : null}
        </div>
      </div>

      <HairReportNav
        onBack={handleBack}
        onHome={handleHome}
        sessionId={sessionId}
        locationState={location.state}
        cartCount={cartCount}
        orgLogoUrl={orgLogoUrl}
        showLogo={showLogo}
        onLogoError={onLogoError}
        orgName={orgName}
      />

      <main className="hair-report-main">
        <section className="hair-report-print-section" data-print-section="diagnostic">
          <h2 className="hair-report-print-section-title">Diagnostic Findings</h2>
          <HairDiagnosticSection
            hairResults={hairResults}
            dbRecommendations={dbRecommendations}
            recsLoading={recsLoading}
            onAddToCart={handleAddToCart}
          />
        </section>

        <div className="hair-report-section-divider" aria-hidden="true">
          <span>Recommended services</span>
        </div>

        <section className="hair-report-print-section" data-print-section="services">
          <HairInsightReportTreatmentsSection
            treatments={recommendedTreatments}
            loading={treatmentsLoading}
            error={treatmentsError}
            sessionId={sessionId}
            token={token}
            treatmentCartItems={hairServiceSelections}
            onTreatmentCartChange={setHairServiceSelections}
          />
        </section>

        <div className="hair-report-section-divider" aria-hidden="true">
          <span>Magnified Scalp Analysis</span>
        </div>

        <section className="hair-report-print-section" data-print-section="magnified">
          <h2 className="hair-report-print-section-title">Magnified Scalp Analysis</h2>
          <MagnifiedAnalysisSection magnifiedAnalysis={hairResults.magnified_analysis} />
        </section>

        <div className="hair-report-section-divider" aria-hidden="true">
          <span>Your Personalized Routine</span>
        </div>

        <section className="hair-report-print-section" data-print-section="routine">
          <h2 className="hair-report-print-section-title">Personalized Care Routine</h2>
          <HairRoutineSection
            hairResults={hairResults}
            aqiData={aqiData}
            routineData={routineData}
            sessionId={sessionId}
            isLoading={routineLoading}
          />
        </section>
      </main>

      {treatmentCartTotal > 0 ? (
        <div className="skin-routine-next-treatments-inline hair-report-service-confirm" data-pdf-exclude="true">
          <button
            type="button"
            className="skin-action-button skin-routine-continue-treatments-button"
            disabled={confirmBusy}
            onClick={handleConfirmHairServices}
          >
            {confirmBusy ? 'CONFIRMING…' : `CONFIRM SERVICES (${treatmentCartTotal})`}
          </button>
          {serviceConfirmNotice ? (
            <p className="skin-routine-summary-text" role="status">{serviceConfirmNotice}</p>
          ) : null}
        </div>
      ) : null}

      <div className="hair-report-print-footer" aria-hidden="true">
        <span>Confidential — for informational purposes only. Not a medical diagnosis.</span>
        <span>Powered by nyafaz</span>
      </div>

      <CartAddedToast
        visible={cartToastVisible}
        onClose={() => setCartToastVisible(false)}
      />

      <PageFooter />
    </div>
  );
}

export default HairReportPage;
